import { plugin, debug, lang } from 'light-component';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Space, TreeSelect } from 'antd';
import { Require, Select } from '../component';
import { t } from '../../i18n';
import { TreeService } from '../../tree';
import { convert } from '../../icons/icons';
import styles from './style.module.less';
import type { AreaStoreSelectProps, StoreOption } from './types';

const { SHOW_ALL } = TreeSelect;

export function WidgetAreaStoreSelect(props: AreaStoreSelectProps): ReactElement {
  debug.widget('dashboard AreaStore', props);

  const areaOption = props.options?.filter(item => item.type === 'area');
  const storeOption = props.options?.filter(item => item.type === 'store');

  let allOptions = storeOption?.map?.(item => ({
    value: item._id,
    label: `${item.name}（${item.outer}）`,
    tags: item.tags,
    parent: item.parent,
    state: item.state,
    idPath: item.idPath
  })) ?? [];

  const treeData = areaOption?.map(item => ({
    ...item,
    icon: convert('AreaSvgIcon'),
    groupType: 'area' as const
  }));

  // 给门店的option去重
  const uniqueValue = new Set(allOptions?.map(item => item.value));
  allOptions = allOptions?.filter(item => uniqueValue.delete(item.value));

  const tagOptions = props.tagOptions?.map?.(item => ({
    value: item.value,
    label: item.title as string
  })) ?? [];

  const [storeValue, setStoreValue] = useState<string[]>([]);
  const [areaValue, setAreaValue] = useState<string[]>([]);
  const [state, setState] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [options, setOptions] = useState<StoreOption[]>([]);

  const valueChange = useRef<boolean>(false);

  const handleAreaChange = (value: string[]): void => {
    valueChange.current = true;
    setAreaValue(value);
    setStoreValue([]);
    if (value == null || value.length === 0) {
      props.onChange?.({
        stores: [],
        areas: []
      });
      return;
    }

    const storeValue = storeOption.filter(item => value.includes(item.parent));
    props.onChange?.({
      stores: storeValue.map(item => item._id),
      areas: value
    });
  };

  const handleStoreChange = (value: string[]): void => {
    valueChange.current = true;
    setStoreValue(value);
    if (value == null || value.length === 0) {
      if (areaValue == null || areaValue.length === 0) {
        props.onChange?.({
          stores: [],
          areas: []
        });
        return;
      }
      const storeValue = storeOption.filter(item => areaValue.includes(item.parent));
      props.onChange?.({
        stores: storeValue.map(item => item._id),
        areas: areaValue
      });
      return;
    }
    props.onChange?.({
      stores: value,
      areas: areaValue
    });
  };

  const onStateChange = (value: string[]): void => {
    setState(value);
  };

  const onLabelsChange = (value: string[]): void => {
    setTags(value);
  };

  const updateOption = (): void => {
    let option = [...allOptions];
    if (tags.length > 0) {
      option = option.filter(option => tags.some(item => option.tags?.includes(item)));
    }
    if (state.length > 0) {
      option = option.filter(item => state.includes(item.state));
    }
    if (areaValue.length > 0) {
      option = option.filter(item => lang.intersection(areaValue, item.idPath)?.length > 0);
    }
    setOptions(option);
  };

  useEffect(() => {
    updateOption();
  }, [props.options, tags, state, areaValue]);

  useEffect(() => {
    if (valueChange.current) {
      valueChange.current = false;
      return;
    }
    if (props.value != null) {
      setStoreValue([...props.value.stores ?? []]);
      setAreaValue([...props.value.areas ?? []]);
    }
  }, [props.value]);

  return (
    <div className={props.className ?? styles.propertyItem}>
      <div className={styles.input}>
        <Require require={props.require} />
        <span className={styles.title}>{t(props.title)}</span>
      </div>
      <Space>
        <TreeSelect
          className={styles.formSelectBox}
          treeIcon
          value={areaValue}
          placeholder={t('请选择分区')}
          showSearch
          allowClear
          autoClearSearchValue
          size='middle'
          treeCheckable
          treeNodeFilterProp='title'
          treeDefaultExpandedKeys={[props.options?.find(item => item.parent === '')?._id ?? '']}
          treeLine={{ showLeafIcon: false }}
          popupMatchSelectWidth={false}
          treeData={new TreeService().toTree(treeData ?? [])}
          onChange={handleAreaChange}
          maxTagCount={1}
          showCheckedStrategy={SHOW_ALL}
        />

        <Select
          className={styles.formSelectBox}
          mode='multiple'
          value={storeValue}
          placeholder={t(props.placeholder ?? '')}
          options={options}
          allowClear
          selectAll={storeValue.length === options.length}
          onChange={handleStoreChange}
          skipFilterInvalid
          autoClearSearchValue={false}
          dropdownRender={(menu: React.ReactElement) => <>
            <Space direction='vertical' className={styles.storeFilter} >
              <Space>
                <span>
                  {t('门店状态')}
                </span>
                <div
                  onMouseDown={event => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <Select
                    value={state}
                    options={[
                      { label: t('正常营业'), value: 'normal' },
                      { label: t('营建中'), value: 'construct' },
                      { label: t('暂停营业'), value: 'suspended' },
                      { label: t('闭店'), value: 'defunct' }
                    ]}
                    selectAll={state?.length === 4}
                    onChange={onStateChange}
                    mode='multiple'
                    allowClear
                    className={styles.innerSelectBox}
                  />
                </div>
              </Space>
              <Space>
                <span>
                  {t('门店标签')}
                </span>
                <div
                  onMouseDown={event => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <Select
                    value={tags}
                    options={tagOptions}
                    selectAll={tags?.length === tagOptions.length}
                    onChange={onLabelsChange}
                    mode='multiple'
                    allowClear
                    className={styles.innerSelectBox}
                  />
                </div>
              </Space>
            </Space>
            {menu}
          </>
          }
        />
      </Space>
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'areaStoreSelect', component: WidgetAreaStoreSelect });
